import { useRef } from "react";
import type { ComponentProps } from "react";
import { useIntl } from "react-intl";
import { DatePicker } from "./DatePicker";
import { displayText, displayValue } from "./display-value";

// Inline-edit primitive. In edit mode every field is a real, always-mounted
// <input>/<textarea> - never a <button> - so keyboard Tab reaches it on every
// platform (macOS drops <button>s from the Tab sequence unless "Keyboard
// navigation" is enabled, which would otherwise make tabbing skip straight from
// one <select> to the next). At rest the control is styled to read as plain
// text; on hover and focus it lifts to the active look - white
// fill, ink text, hairline grey ring. `editable=false` renders read-only text
// with no edit affordance.

// `email` / `tel` carry no styling of their own — they exist for the semantic input type and the
// mobile keyboard it selects. Deliberately no `url`: native url validation demands a scheme, so the
// scheme-less "smycles.com" users actually type would read as invalid. Website is a `text` field
// whose shape is checked by `importFieldError` instead.
export type Kind = "text" | "date" | "number" | "email" | "tel";

// The wrap shape is a textarea, and a textarea has no `type`. These attributes carry over what
// `email` / `tel` would have picked on a phone: the keyboard, and for an address no capital first
// letter and no spell check.
const WRAP_KEYBOARD: Partial<
  Record<Kind, Pick<ComponentProps<"textarea">, "inputMode" | "autoCapitalize" | "spellCheck">>
> = {
  email: { inputMode: "email", autoCapitalize: "off", spellCheck: false },
  tel: { inputMode: "tel" },
};

interface InlineEditProps {
  value: string | null | undefined;
  onChange: (next: string) => void;
  // Fires once when focus leaves the field (blur) or on Enter / Cmd+Enter.
  // Receives the committed value. Used to trigger a save without saving on
  // every keystroke. Not fired on Escape (which reverts).
  onCommit?: (committed: string) => void;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  multiline?: boolean;
  type?: Kind;
  // Numbers for `type="number"`, ISO `YYYY-MM-DD` strings for `type="date"` (a date input's native
  // bounds are date strings, not numbers).
  min?: number | string;
  max?: number | string;
  // Deliberately NO `maxLength`: it silently swallows keystrokes and truncates paste, leaving the user
  // to guess why input stopped. Length is surfaced instead by `importFieldError` + <MergeFieldWarning>
  // and enforced by the save gate — the same mechanism the in-line import review uses.
  editable?: boolean;
  // When false, drops the × clear button and the right-side space it reserves,
  // so the field hugs its content. Use for tightly-spaced inline fields like
  // the given/family name pair, where a per-field clear button is unnecessary
  // and the reserved gap reads as unnaturally wide.
  clearable?: boolean;
  // When true, the field wraps long content onto multiple lines (capped at the
  // container width) instead of growing one line past its bounds. It still hugs
  // its content while it fits, so short values sit inline next to a sibling.
  // Enter commits (no newline). Used for the profile name pair and for every text, email and phone
  // field of the profile (BasicField), whose grid cells are too narrow for one line.
  wrap?: boolean;
  // Optional DOM id set on the underlying input/textarea — a stable,
  // locale-independent hook for e2e tests (spark-validation).
  id?: string;
  // Accessible name for the underlying input/textarea. Needed when the field
  // has no visible label of its own (e.g. one side of the LinkedIn merge pair).
  ariaLabel?: string;
}

// Shared by the single-line and the wrap shape; the caller positions it.
function ClearButton({ onClear, className }: { onClear: () => void; className: string }) {
  const intl = useIntl();
  const label = intl.formatMessage({ id: "design.clearValue", defaultMessage: "Clear value" });
  return (
    <button
      type="button"
      tabIndex={-1}
      onMouseDown={(e) => {
        // preventDefault stops the click from blurring the input (which
        // would fire onCommit with the OLD value before we clear).
        e.preventDefault();
        onClear();
      }}
      aria-label={label}
      title={label}
      className={`z-10 flex h-4 w-4 items-center justify-center rounded-full text-smycles-body leading-none text-ink-muted opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100 hover:bg-ink/10 hover:text-ink ${className}`}
    >
      × {/* i18n-allow */}
    </button>
  );
}

export function InlineEdit({
  value,
  onChange,
  onCommit,
  className = "",
  inputClassName = "",
  placeholder = "–",
  multiline = false,
  type = "text",
  min,
  max,
  editable = true,
  clearable = true,
  wrap = false,
  id,
  ariaLabel,
}: InlineEditProps) {
  const intl = useIntl();
  // Captured on focus so Escape can revert to the value the field held when
  // editing began.
  const originalRef = useRef(value);
  // Set by Escape so the synchronous blur that follows commits the REVERTED
  // value: the `value` prop is still the pre-Escape text at that point (React
  // has not re-rendered), and committing that stale text would let a caller
  // act on an edit the user just aborted.
  const escapedRef = useRef(false);

  if (!editable) {
    const display = displayValue(value, type);
    // A multiline value keeps its line breaks in the read-only state — otherwise
    // a locked textarea field (e.g. the profile summary during a LinkedIn merge
    // review) flattens the user's paragraphs into one run-on line.
    if (multiline) {
      return (
        <span id={id} className={`block min-w-0 whitespace-pre-wrap break-words ${className}`}>
          {display ? display : <span className="text-ink-muted">{placeholder}</span>}
        </span>
      );
    }
    // The value sits in its own min-w-0 flex item: the anonymous item a bare text would get keeps
    // min-width:auto, so a long word could never shrink below its full width and break.
    return (
      <span id={id} className={`inline-flex min-w-0 items-baseline ${className}`}>
        {display ? (
          <span className="min-w-0 break-words">{displayText(value, type)}</span>
        ) : (
          <span className="text-ink-muted">{placeholder}</span>
        )}
      </span>
    );
  }

  if (type === "date") {
    return (
      <span className={`inline-flex min-w-0 items-center gap-1 ${className}`}>
        <InlineEdit
          id={id}
          ariaLabel={ariaLabel}
          value={value}
          onChange={onChange}
          onCommit={onCommit}
          min={min}
          max={max}
          placeholder={placeholder}
          clearable={clearable}
          inputClassName={inputClassName}
          className="min-w-0"
        />
        <DatePicker
          triggerVariant="icon"
          aria-label={ariaLabel ? `${ariaLabel} — ${intl.formatMessage({ id: "datePicker.chooseDate", defaultMessage: "Choose date" })}` : undefined}
          value={value}
          min={min == null ? undefined : String(min)}
          max={max == null ? undefined : String(max)}
          required={!clearable}
          onChange={(next) => {
            onChange(next);
            onCommit?.(next);
          }}
        />
      </span>
    );
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      onCommit?.(value ?? "");
      e.currentTarget.blur();
    } else if (e.key === "Enter" && multiline && e.metaKey) {
      e.preventDefault();
      onCommit?.(value ?? "");
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      escapedRef.current = true;
      onChange(originalRef.current ?? "");
      e.currentTarget.blur();
    }
    // Tab is left to native handling → focus follows DOM order across all fields.
  };

  const onFocus = () => {
    originalRef.current = value;
  };
  const onBlur = () => {
    const committed = escapedRef.current ? (originalRef.current ?? "") : (value ?? "");
    escapedRef.current = false;
    onCommit?.(committed);
  };

  // Hover marks editable values; focus lifts
  // it to the active look - white fill, ink text, grey ring. The grey (not the
  // accent signal colour, which flags mandatory fields) keeps focus neutral.
  const stateCls =
    "bg-transparent text-ink ring-1 ring-transparent placeholder:text-ink-muted transition hover:bg-ink/5 focus:bg-surface focus:text-ink focus:outline-none focus:ring-ink-ui";

  // Native number spinners have no place here, and not for taste: the ghost below sizes the field
  // from the TEXT, and the stepper arrows are ~15px of chrome the ghost cannot know about, so a
  // field showing "234" clipped it to "23". Typing and Arrow-Up/Down still work — only the buttons
  // are gone.
  const numberCls =
    type === "number"
      ? "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      : "";

  // Multiline grows with its text, so a long text never hides behind three rows and a scrollbar
  // that reads as "only a little text here". Three lines stay as the floor (`rows` no longer sizes a
  // content-sized field), and 60vh is the ceiling: past it the field scrolls, so the field and its
  // surroundings stay on one screen. No resize grip: against content sizing a drag snaps back.
  if (multiline) {
    return (
      <textarea
        id={id}
        aria-label={ariaLabel}
        rows={3}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={`block w-full resize-none rounded px-1.5 py-0.5 field-sizing-content min-h-[calc(3lh+0.25rem)] max-h-[60vh] ${stateCls} ${inputClassName} ${className}`}
      />
    );
  }

  // When the field has a value, it reserves ~24px on the right (`pr-6`) so the × clear button sits
  // inside the field's visible area without overlapping the text.
  const hasValue = Boolean(value && value !== "");
  const showClear = hasValue && clearable;
  const valuePadding = showClear ? "pr-6" : "";
  // onCommit("") right after the change, so the SaveBar still gets a definitive committed value for
  // the cleared field.
  const clear = () => {
    onChange("");
    onCommit?.("");
  };

  // Wrapping single-logical-line: same ghost trick as the single-line case, but
  // the ghost wraps (whitespace-pre-wrap + break-words) and the span is capped
  // at the container width, so the ghost defines BOTH width (hug while it fits,
  // else full width) and height (1 line, or more when wrapped). The cap counts
  // the -mx-1.5 in: the box reaches 6px past each edge, so its text spans the
  // container exactly. A plain max-w-full left it 12px short, which in a
  // container as wide as its content (a BoxHeader title) wrapped a name that
  // fits. The overlaid textarea follows that box. Enter still commits
  // (onKeyDown sees multiline=false), so it behaves as one logical line that
  // merely wraps.
  if (wrap) {
    const wrapGhost = value && value !== "" ? value : placeholder;
    return (
      <span className="group relative -mx-1.5 inline-grid max-w-[calc(100%+0.75rem)] grid-cols-[minmax(0,max-content)] align-baseline">
        <span
          aria-hidden="true"
          className={`pointer-events-none invisible col-start-1 row-start-1 whitespace-pre-wrap break-words px-1.5 py-0.5 ${valuePadding} ${className} ${inputClassName}`}
        >
          {wrapGhost || " "}
        </span>
        <textarea
          id={id}
          aria-label={ariaLabel}
          rows={1}
          cols={1}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          {...WRAP_KEYBOARD[type]}
          className={`col-start-1 row-start-1 h-full w-full resize-none overflow-hidden break-words rounded px-1.5 py-0.5 ${valuePadding} ${stateCls} ${inputClassName} ${className}`}
        />
        {/* Centred on the FIRST line, not on the whole field: the strip carries the field's own
            type classes, so 1lh is the field's line height, and py-0.5 matches the textarea's. */}
        {showClear ? (
          <span
            className={`pointer-events-none absolute right-1 top-0 flex h-[calc(1lh+0.25rem)] items-center py-0.5 ${className} ${inputClassName}`}
          >
            <ClearButton onClear={clear} className="pointer-events-auto" />
          </span>
        ) : null}
      </span>
    );
  }

  // Single-line: size the input to its content via an invisible "ghost" copy of
  // the value (or placeholder when empty), so inline fields (e.g. the centred
  // name) don't snap to the browser's default input width. The input is taken
  // OUT OF FLOW over the ghost (absolute inset-0) rather than sharing grid-area
  // 1/1 with it: an in-flow input contributes its own intrinsic width to the
  // grid column, and for `type="number"` that intrinsic width is the browser's
  // ~170px default — `size={1}` is ignored on anything but text/search/url/tel/
  // email/password — so a two-digit number field rendered a hand-wide gap next
  // to whatever followed it. Out of flow it contributes nothing and the ghost
  // alone decides the box. `-mx-1.5` cancels the input's own padding so the text stays flush
  // with the surrounding label.
  const ghost = value && value !== "" ? value : placeholder;
  return (
    <span className="group relative -mx-1.5 inline-grid align-baseline">
      <span
        aria-hidden="true"
        className={`pointer-events-none invisible col-start-1 row-start-1 whitespace-pre px-1.5 py-0.5 ${valuePadding} ${className} ${inputClassName}`}
      >
        {ghost || " "}
      </span>
      <input
        id={id}
        aria-label={ariaLabel}
        type={type}
        size={1}
        min={min}
        max={max}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={`absolute inset-0 w-full rounded px-1.5 py-0.5 ${valuePadding} ${stateCls} ${numberCls} ${inputClassName} ${className}`}
      />
      {showClear ? <ClearButton onClear={clear} className="absolute right-1 top-1/2 -translate-y-1/2" /> : null}
    </span>
  );
}
